import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { AuthService } from '@service/auth.service';
import { ApiResponse } from '@type/response.type';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private accessTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private authService: AuthService,
    private readonly router: Router,
  ) { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const errorResponse = error?.error as ApiResponse;

        if (error.status !== 401 || !this.authService.authToken?.refresh || errorResponse?.internalCode === 'invalid_verification_code') {
          return throwError(() => error);
        }

        if (this.isRefreshing) {
          return this.accessTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(token => next.handle(this.addToken(req, token))),
          );
        }

        this.isRefreshing = true;
        this.accessTokenSubject.next(null);

        return this.authService.refreshToken().pipe(
          switchMap(authToken => {
            this.isRefreshing = false;
            this.accessTokenSubject.next(authToken.access);
            return next.handle(this.addToken(req, authToken.access));
          }),
          catchError(refreshError => {
            this.isRefreshing = false;
            this.router.navigateByUrl('/login');
            return throwError(() => refreshError);
          }),
        );
      }),
    );
  }

  private addToken(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }
}
